import { useClosedLiftScopes } from '../../api/hooks/useLiftRules';
import { useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRobots, useTasks } from '../../api/hooks';
import { commissionKey, useRobotCommissionStates } from '../../api/hooks/useCommissionRobot';
import { useCleaningMetrics } from '../../api/hooks/useCleaningMetrics';
import { useDeviceNotes } from '../../api/hooks/useDeviceNotes';
import { useGateways } from '../../api/hooks/useGateways';
import { usePlaces } from '../../api/hooks/usePlaces';
import { useRobotGroups } from '../../api/hooks/useRobotGroups';
import { useScheduleOccurrences } from '../../api/hooks/useSchedules';
import { DataTable, type DataTableColumn } from '../../design-system/components/DataTable';
import {
  AttentionStrip,
  ScreenTabs,
  ScreenTitleRow,
  StatusCell,
  SummaryLine,
  TableToolbar,
  type AttentionItem,
  type SummaryFact,
} from '../../design-system/components/ScreenParts';
import { StatusChip } from '../../design-system/components/StatusChip';
import { EmptyState } from '../../design-system/components/EmptyState';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { RobotDetailDrawer } from './RobotDetailDrawer';
import { useActiveSite } from '../../state/useActiveSite';
import { scopeByActiveSite } from '../../api/scope';
import type { RobotState, TaskState } from '../../api/rmf-client';
import type { RobotCommissionState } from '../../api/v1';
import {
  ALERT_DEEP_LINK_EVENT,
  robotForDeepLink,
  type AlertDeepLinkKind,
} from '../../state/alert-deep-link';
import { useLifts } from '../../api/hooks/useLifts';
import { useFeedHealth } from '../../api/feed-health';
import { robotReceiptKey, useRobotReceipts } from '../../api/robot-receipts';
import { AuthContext } from '../../auth/AuthContext';
import { RoleGuard } from '../../auth/RoleGuard';
import { audienceFor } from '../../auth/roles';
import { TASK_DISPATCH_ROLES } from '../../auth/types';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { getOptionalRuntimeConfig } from '../../config/runtime-config';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import { isSimulatedFleet } from '../../state/simulated-fleet';
import { BATTERY_LOW_PCT } from '../../state/battery-levels';
import { useMinuteTick, useNowTick } from '../../state/clock';
import { useRobotPresence } from '../Map/useRobotPresence';
import { METRICS_ROLES, SITE_SETTINGS_VIEW_ROLES } from '../Metrics/metrics-access';
import { attentionFlagOn } from '../Overview/attention/attention-readings';
import { formatClock } from '../Devices/lift-reading';
import { gatewayFacts, naturalKey } from '../Devices/devices-rows';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import {
  autoChargeWords,
  doingWords,
  fleetAttention,
  fleetSummary,
  nextRunFor,
  outOfServiceWords,
  robotStatusCell,
  robotTaskOf,
  siteDateIso,
  statusRank,
  whereWords,
  type RobotStatusClass,
  type RobotStatusCell,
} from './fleet-rows';
import { batteryText, heardText, isReportingNow, lastReportAtMs } from './robot-lines';
import './FleetScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { HelpScreenNote } from '../../help';

const NAME_SORT = { columnId: 'name', direction: 'asc' } as const;

function batteryColor(battery: number | undefined): string {
  if (battery == null) return 'var(--nx-muted)';
  if (battery < BATTERY_LOW_PCT) return 'var(--nx-error)';
  if (battery < 50) return 'var(--nx-warning)';
  return 'var(--nx-cyan)';
}

// Same de-emphasis the rest of the app already uses for
// present-but-inapplicable content (`TaskDetailDrawer.css`,
// `DeviceDetailDrawers.css`, disabled controls). A robot out of service
// is still real and still worth reading — it just is not available to
// take work — so it dims rather than greying out to an unreadable value
// or disappearing behind a filter.
const DECOMMISSIONED_CELL: CSSProperties = { opacity: 0.45 };

/** Holds `robot.commission` (`RobotDetailDrawer.tsx::COMMISSION_ROLES`):
 *  who is offered *Back in service…*, which opens the robot's panel. */
const COMMISSION_OFFER_ROLES: readonly string[] = ['nexsoss:platform_admin', 'nexsoss:site_supervisor'];

type StatusFilter = 'all' | 'working' | 'charging' | 'needs_help' | 'out_of_service';

const STATUS_FILTERS: readonly { id: StatusFilter; label: string; classes?: readonly RobotStatusClass[] }[] = [
  { id: 'all', label: 'All' },
  { id: 'working', label: 'Working', classes: ['working', 'paused'] },
  { id: 'charging', label: 'Charging', classes: ['charging'] },
  { id: 'needs_help', label: 'Needs help', classes: ['needs_help'] },
  { id: 'out_of_service', label: 'Out of service', classes: ['out_of_service'] },
];

/** The look-ahead for "next run": today's and tonight's runs, no further. */
const NEXT_RUN_WINDOW_MS = 24 * 60 * 60 * 1000;

export function FleetScreen() {
  const robots = useRobots();
  const [groupTab, setGroupTab] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  // Selection is tracked by NAME, not by storing the clicked row's
  // view-model object directly — a stored object is a snapshot frozen
  // at click time and would never again reflect live updates (status,
  // location, current task, ...) for as long as the drawer stayed
  // open. Same fix already applied to `DevicesScreen.tsx`'s door/lift
  // selection; this screen had the identical bug.
  const [selectedRobotName, setSelectedRobotName] = useState<string | null>(null);
  const navigate = useNavigate();
  const { site, sites } = useActiveSite();
  const auth = useContext(AuthContext);
  const audience = audienceFor(auth?.roles);
  // A site audience never sees the simulated fleet: not in the table, not
  // in the fleet tabs, not through a deep link (`canSeeSimulated`).
  const showSimulated = useCanSeeSimulated();
  const visibleRobots = useMemo(
    () => (robots.data ?? []).filter((r) => showSimulated || !isSimulatedFleet(r.fleet)),
    [robots.data, showSimulated],
  );
  const selectedRobot = visibleRobots.find((r) => r.name === selectedRobotName) ?? null;

  // Listen for deep-link clicks from the alert bell + toast. The
  // AlertNotificationsProvider dispatches `nx:alert-deep-link` when
  // the user clicks a robot alert; the matching drawer opens here.
  useEffect(() => {
    function onDeepLink(e: Event) {
      const ce = e as CustomEvent<AlertDeepLinkKind>;
      const detail = ce.detail;
      if (!detail || detail.kind !== 'robot') return;
      const match = robotForDeepLink(visibleRobots, detail.robotName);
      if (match) setSelectedRobotName(match.name);
    }
    window.addEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
    return () => window.removeEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
  }, [visibleRobots]);

  // Live MQTT updates are managed app-wide by `useLiveData` in
  // `AppShell`. The shared `['robots']` TanStack Query cache is kept
  // current regardless of which tab is mounted.

  // The `useLiveData` overlay already subscribes to the active site's
  // topics, so the cache is pre-scoped. We additionally filter here
  // for defense-in-depth via `scopeByActiveSite`: entities without a
  // `siteId` are treated as belonging to all sites (backward
  // compatibility with upstream APIs that don't tag entities).
  const scopedRobots = useMemo(
    () => scopeByActiveSite(visibleRobots, site?.id ?? ''),
    [visibleRobots, site?.id],
  );

  // Each row says only what the robot's presence backs (`robot-lines.ts`):
  // the same classification the map draws and the drawer reads.
  const lifts = useLifts().data;
  const presence = useRobotPresence(scopedRobots, lifts);
  const presenceOf = (r: RobotState) => presence.get(robotReceiptKey(r.fleet, r.name));
  const feed = useFeedHealth(site?.id ?? '');
  // "today" vs "yesterday" turns over at midnight.
  const wallNowMs = useMinuteTick();
  // "12 min left" and "heard 4 s ago" stay current between live updates.
  const nowMs = useNowTick(5_000);
  const zone = resolveSiteTimeZone(sites, site?.id);
  const siteZone = zone.source === 'site' ? zone.timeZone : null;
  const clock = (ms: number) => formatClock(ms, siteZone, wallNowMs);

  const { book: receipts } = useRobotReceipts(site?.id ?? '');
  const heardOf = (r: RobotState) => {
    const receipt = receipts.get(robotReceiptKey(r.fleet, r.name));
    return heardText(presenceOf(r), {
      agoMs: receipt.lastAt === null ? null : receipts.now() - receipt.lastAt,
      count: receipt.count,
    });
  };

  // `commission_state` is a registry field (the `fleets` category),
  // not part of the live robot state this screen otherwise renders —
  // see `useRobotCommissionStates`. A robot the registry hasn't
  // reported reads 'unknown' rather than being assumed in service.
  const commissionStates = useRobotCommissionStates();
  const commissionInfoOf = (r: RobotState) =>
    commissionStates.data?.get(commissionKey(r.fleet, r.name));
  const commissionOf = (r: RobotState): RobotCommissionState =>
    commissionInfoOf(r)?.state ?? 'unknown';
  const notes = useDeviceNotes().data;

  const tasks = useTasks().data;
  const taskOf = (r: RobotState): TaskState | null => robotTaskOf(r, tasks ?? []);
  const places = usePlaces().data ?? [];
  // From the top of the hour, so the read changes hourly rather than with
  // every minute tick; `nextRunFor` drops what has already gone by.
  const occurrenceFromMs = Math.floor(wallNowMs / 3_600_000) * 3_600_000;
  const occurrenceWindow = useMemo(
    () => ({ fromMs: occurrenceFromMs, toMs: occurrenceFromMs + NEXT_RUN_WINDOW_MS }),
    [occurrenceFromMs],
  );
  const occurrences = useScheduleOccurrences(occurrenceWindow).data?.occurrences ?? [];

  const groupsQuery = useRobotGroups(site?.id);
  const groupData = groupsQuery.data ?? null;

  const statusOf = (r: RobotState): RobotStatusCell =>
    robotStatusCell({
      robot: r,
      presence: presenceOf(r),
      clamped: feed.clamped,
      commission: commissionOf(r),
      issues: commissionInfoOf(r)?.issues ?? [],
      task: taskOf(r),
      audience,
      outOfServiceWords: outOfServiceWords(
        notes?.robots_out.find((n) => n.fleet === r.fleet && n.robot === r.name),
        clock,
      ),
    });
  const liftClosed = useClosedLiftScopes();
  const doingOf = (r: RobotState) => {
    const task = taskOf(r);
    // A robot out of service takes no run, so none is next for it.
    const takesRuns = commissionOf(r) !== 'decommissioned';
    return doingWords({
      task,
      nextRun: task || !takesRuns ? null : nextRunFor(r, occurrences, nowMs),
      clock,
      liftClosed,
    });
  };
  const whereOf = (r: RobotState) => whereWords(presenceOf(r), places, siteZone, wallNowMs);
  const batteryOf = (r: RobotState) => batteryText(r.battery, presenceOf(r), feed.clamped);

  // -- Tabs: the site's robot groups. One group needs no tab bar. --------
  const groupTabs = useMemo(() => {
    const keys = new Set(scopedRobots.map((r) => `${r.fleet}/${r.name}`));
    return (groupData?.groups ?? [])
      .map((g) => ({ id: g.id, label: g.name, robots: g.robots.filter((k) => keys.has(k)) }))
      .filter((g) => g.robots.length > 0);
  }, [groupData, scopedRobots]);
  const activeGroup = groupTabs.length > 1 ? (groupTabs.find((g) => g.id === groupTab) ?? groupTabs[0]!) : null;
  const inGroup = (r: RobotState) => !activeGroup || activeGroup.robots.includes(`${r.fleet}/${r.name}`);

  const rows = scopedRobots.filter((r) => {
    if (!inGroup(r)) return false;
    if (search.trim() && !r.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    const filter = STATUS_FILTERS.find((f) => f.id === statusFilter);
    return !filter?.classes || filter.classes.includes(statusOf(r).cls);
  });

  // -- Summary line -----------------------------------------------------
  const summaryRobots = scopedRobots.map((r) => ({
    name: r.name,
    cell: statusOf(r),
    battery:
      isReportingNow(presenceOf(r), feed.clamped) && typeof r.battery === 'number' ? r.battery : null,
  }));
  const summary = fleetSummary(summaryRobots);
  const gateways = useGateways().data ?? [];
  // Metrics is not everyone's: the front desk's read is refused, so it
  // does not ask, and the fact is left out rather than shown as nothing.
  const seesMetrics = auth?.roles?.some((role) => METRICS_ROLES.includes(role)) === true;
  const today = siteDateIso(wallNowMs, siteZone);
  const cleaning = useCleaningMetrics({ from: today, to: today }, seesMetrics);
  const cleanedToday = cleaning.data?.grid.zones.filter((z) => z.days[z.days.length - 1] === 'cleaned').length;
  const facts: SummaryFact[] = [
    ...summary.facts,
    ...gatewayFacts(gateways.filter((g) => g.service === 'robot')),
    ...(typeof cleanedToday === 'number'
      ? [{ id: 'cleaned', label: `Cleaned today ${cleanedToday} ${cleanedToday === 1 ? 'zone' : 'zones'}`, tone: 'quiet' as const }]
      : []),
  ];

  // -- Needs attention, under the Overview's own flags (Akash, 2026-11-02):
  // in production that means ops only until site staff are switched on.
  const showAttention = attentionFlagOn(auth?.roles, getOptionalRuntimeConfig()?.featureFlags);
  const attention: AttentionItem[] = showAttention
    ? fleetAttention(summaryRobots).map((item) => ({
        id: item.id,
        severity: item.severity,
        title: item.title,
        detail: item.detail,
        onDetails: () => setSelectedRobotName(item.robot),
      }))
    : [];

  const canCommission = auth?.roles?.some((role) => COMMISSION_OFFER_ROLES.includes(role)) === true;
  const dim = (r: RobotState, node: ReactNode): ReactNode =>
    commissionOf(r) === 'decommissioned' ? <span style={DECOMMISSIONED_CELL}>{node}</span> : node;

  const renderBattery = (r: RobotState) => {
    const shown = batteryOf(r);
    const known = shown !== '—' && typeof r.battery === 'number';
    return (
      <span className="nx-fleet__battery">
        <span className="nx-fleet__battery-line">
          {known && (
            <span className="nx-fleet__battery-bar" aria-hidden="true">
              <span
                className="nx-fleet__battery-fill"
                style={{ width: `${Math.max(0, Math.min(100, r.battery!))}%`, background: batteryColor(r.battery) }}
              />
            </span>
          )}
          <span data-testid={`fleet-battery-${r.name}`}>{shown}</span>
        </span>
      </span>
    );
  };
  // Automatic charging off: a robot otherwise fully in service, so it is a
  // note under the battery rather than a status. Left off a robot out of
  // service, whose flags are all false as a consequence: the status already
  // says it is going nowhere.
  const autoChargeNote = (r: RobotState) => {
    if (commissionInfoOf(r)?.idleBehavior !== false || commissionOf(r) === 'decommissioned') return null;
    return (
      <span className="nx-cell-small" data-testid={`robot-autocharge-off-${r.name}`}>
        {autoChargeWords(notes?.auto_charge_off.find((n) => n.fleet === r.fleet && n.robot === r.name))}
      </span>
    );
  };

  const columns: DataTableColumn<RobotState>[] = [
    {
      id: 'name',
      header: 'Robot',
      width: '12%',
      cell: (r) =>
        dim(
          r,
          <>
            <strong>{r.name}</strong>
            {audience === 'ops' && <span className="nx-cell-small">{r.fleet}</span>}
          </>,
        ),
      sortable: true,
      sortValue: (r) => naturalKey(r.name),
    },
    {
      id: 'status',
      header: 'Status',
      width: '20%',
      cell: (r) => {
        const cell = statusOf(r);
        return (
          <StatusCell
            status={cell.status}
            label={cell.label}
            small={cell.small}
            testId={`fleet-status-${r.name}`}
          />
        );
      },
      sortable: true,
      sortValue: (r) => statusRank(statusOf(r).cls),
    },
    {
      id: 'doing',
      header: 'Doing',
      width: '21%',
      cell: (r) => dim(r, <span data-testid={`fleet-doing-${r.name}`}>{doingOf(r)}</span>),
      sortable: true,
      sortValue: (r) => doingOf(r),
    },
    {
      id: 'battery',
      header: 'Battery',
      width: '12%',
      cell: (r) => (
        <>
          {dim(r, renderBattery(r))}
          {autoChargeNote(r)}
        </>
      ),
      sortable: true,
      sortValue: (r) => (isReportingNow(presenceOf(r), feed.clamped) ? (r.battery ?? -1) : -1),
    },
    {
      id: 'where',
      header: 'Where',
      width: '15%',
      cell: (r) => {
        // No coordinates here for anyone: they are the robot map's units and
        // read as metres. Ops read them labelled, in the panel's Details.
        return dim(
          r,
          <>
            <span data-testid={`fleet-location-${r.name}`}>{whereOf(r)}</span>
            {/* Its own clicks, so going to the map does not also open the panel. */}
            <span className="nx-fleet__show-where" onClick={(e) => e.stopPropagation()}>
              <ShowWhereLink focus={{ kind: 'robot', fleetName: r.fleet, robotName: r.name }} />
            </span>
          </>,
        );
      },
      sortable: true,
      sortValue: (r) => naturalKey(whereOf(r)),
    },
    {
      id: 'heard',
      header: 'Heard',
      width: '9%',
      cell: (r) =>
        dim(
          r,
          <span data-testid={`fleet-last-report-${r.name}`}>
            {heardOf(r)?.replace(/^heard /, '') ??
              (lastReportAtMs(r, presenceOf(r)) === null ? '—' : clock(lastReportAtMs(r, presenceOf(r))!))}
          </span>,
        ),
      sortable: true,
      sortValue: (r) => lastReportAtMs(r, presenceOf(r)) ?? -1,
    },
    {
      id: 'actions',
      header: '',
      headerLabel: '',
      width: '11%',
      stopsRowClick: true,
      cell: (r) => (
        <span className="nx-fleet__actions">
          {commissionOf(r) === 'decommissioned' && canCommission && (
            <button
              type="button"
              className="nx-screen-btn nx-screen-btn--primary"
              onClick={() => setSelectedRobotName(r.name)}
              data-testid={`fleet-back-in-service-${r.name}`}
            >
              Back in service…
            </button>
          )}
          <button type="button" className="nx-screen-btn" onClick={() => setSelectedRobotName(r.name)}>
            Details
          </button>
        </span>
      ),
    },
  ];

  if (!site) {
    return (
      <div className="nx-fleet">
        <h1 className="nx-section-title">Fleet</h1>
        <NoSiteNotice />
      </div>
    );
  }

  return (
    <div className="nx-fleet">
      <ScreenTitleRow title="Fleet">
        {groupData?.canEdit && (
          <RoleGuard allowedRoles={[...SITE_SETTINGS_VIEW_ROLES]}>
            <button
              type="button"
              className="nx-screen-btn"
              onClick={() => navigate('/site-settings#robot-groups')}
              data-testid="fleet-edit-groups"
            >
              Edit robot groups
            </button>
          </RoleGuard>
        )}
        <RoleGuard allowedRoles={[...TASK_DISPATCH_ROLES]}>
          <button
            type="button"
            className="nx-screen-btn nx-screen-btn--primary"
            onClick={() => navigate('/tasks', { state: { openNewTask: true } })}
            data-testid="fleet-new-task"
          >
            + New task
          </button>
        </RoleGuard>
      </ScreenTitleRow>
      <HelpScreenNote screen="fleet" />

      <SummaryLine headline={summary.headline} facts={facts} label="Fleet summary" testId="fleet-summary" />

      <AttentionStrip items={attention} />

      {activeGroup && (
        <ScreenTabs
          label="Robot groups"
          testIdPrefix="fleet-tab"
          active={activeGroup.id}
          onChange={setGroupTab}
          tabs={groupTabs.map((g) => ({ id: g.id, label: g.label, count: g.robots.length }))}
        />
      )}

      <TableToolbar search={search} onSearch={setSearch} searchLabel="Search robots">
        <div className="nx-fleet__filter-group" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={statusFilter === f.id}
              className={`nx-fleet__filter${statusFilter === f.id ? ' nx-fleet__filter--active' : ''}`}
              onClick={() => setStatusFilter(f.id)}
              data-testid={`fleet-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </TableToolbar>

      {robots.isError && (
        <AlertBanner variant="error" title="Failed to load fleet state">
          {robots.error instanceof Error ? robots.error.message : 'Unknown error'}
        </AlertBanner>
      )}

      {robots.isLoading ? (
        <SkeletonLoader shape="table-row" count={4} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No robots match the current filter"
          description="Clear the search or choose a different status."
        />
      ) : (
        <DataTable
          fixedLayout
          columns={columns}
          rows={rows}
          getRowId={(r) => `${r.fleet}/${r.name}`}
          onRowClick={(r) => setSelectedRobotName(r.name)}
          ariaLabel="Fleet robots"
          defaultSort={NAME_SORT}
          rowTone={(r) => {
            const cell = statusOf(r);
            if (cell.cls !== 'needs_help') return null;
            return cell.help?.band === 'safety' ? 'critical' : 'warning';
          }}
          phone={{
            sortKeys: [
              { id: 'name', label: 'Name' },
              { id: 'status', label: 'Status' },
              { id: 'battery', label: 'Battery' },
              { id: 'heard', label: 'Last heard' },
            ],
            renderRow: (r) => {
              const cell = statusOf(r);
              return (
                <>
                  <span className="nx-phone-row__head">
                    <span className="nx-phone-row__name">{r.name}</span>
                    <StatusChip status={cell.status} label={cell.label} />
                  </span>
                  <span className="nx-phone-row__fact">{doingOf(r)}</span>
                  <span className="nx-cell-small">
                    {[cell.small, whereOf(r), batteryOf(r) === '—' ? null : batteryOf(r)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {autoChargeNote(r)}
                </>
              );
            },
          }}
        />
      )}

      {/* No footer about access. It advertised Recall / Assign / Reboot,
          which do not exist, and told the front desk and service roles
          "Read-only" although they hold controls in the robot drawer
          (2026-10-31). The drawer shows each role what it can do. */}

      {selectedRobot && (
        <RobotDetailDrawer robot={selectedRobot} onClose={() => setSelectedRobotName(null)} />
      )}
    </div>
  );
}
